import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Wifi, Users, Activity, AlertCircle, Search, Filter,
  LogOut, Home, Folder, Radio, User, ChevronRight,
  Signal, Smartphone, RefreshCw, Edit2, Trash2, Move, Loader2, Settings,
  Sparkles
} from 'lucide-react';
import { CHANGELOG_VERSAO_ATUAL, CHANGELOG_HISTORICO } from './changelog';

const API_URL = '/api';
const CHANGELOG_CHAVE_VISTO = 'mgmt_aps_changelog_visto';
const ASSINATURA = "Delvan's";

// ============= COMPONENTES DE UTILIDADE =============

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-lg shadow-md p-6 ${className}`}>
    {children}
  </div>
);

const Button = ({ children, onClick, variant = 'primary', className = '', disabled = false }) => {
  const variants = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white',
    secondary: 'bg-gray-200 hover:bg-gray-300 text-gray-800',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    success: 'bg-green-600 hover:bg-green-700 text-white'
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2 rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${variants[variant]} ${className}`}
    >
      {children}
    </button>
  );
};

const Badge = ({ children, variant = 'default' }) => {
  const variants = {
    success: 'bg-green-100 text-green-800',
    danger: 'bg-red-100 text-red-800',
    warning: 'bg-yellow-100 text-yellow-800',
    default: 'bg-gray-100 text-gray-800'
  };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${variants[variant]}`}>
      {children}
    </span>
  );
};

// ============= AUTENTICAÇÃO =============

const LoginPage = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [switches, setSwitches] = useState([]);
  const [selectedSwitch, setSelectedSwitch] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingSwitches, setLoadingSwitches] = useState(true);

  useEffect(() => {
    fetchSwitches();
  }, []);

  const fetchSwitches = async () => {
    try {
      const response = await fetch(`${API_URL}/switches/list`);
      const data = await response.json();
      setSwitches(data.data || []);
      if (data.data && data.data.length > 0) {
        setSelectedSwitch(data.data[0].id);
      }
    } catch (err) {
      console.error('Erro ao carregar switches:', err);
    } finally {
      setLoadingSwitches(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!selectedSwitch) {
      setError('Selecione um switch');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, switch_id: selectedSwitch })
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('switch_id', selectedSwitch);
        // Salva nome do switch selecionado
        const selectedSwitchData = switches.find(sw => sw.id === selectedSwitch);
        localStorage.setItem('switch_name', selectedSwitchData?.name || 'Switch');
        onLogin(data.token, data.user);
      } else {
        setError(data.message || 'Erro ao fazer login');
      }
    } catch (err) {
      setError('Erro de conexão com o servidor');
    } finally {
      setLoading(false);
    }
  };

  if (loadingSwitches) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
            <p className="text-gray-600">Carregando switches...</p>
          </div>
        </Card>
      </div>
    );
  }

  if (switches.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-yellow-600 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-800 mb-2">Nenhum Switch Configurado</h2>
            <p className="text-gray-600 mb-4">
              É necessário configurar pelo menos um switch antes de fazer login.
            </p>
            <p className="text-sm text-gray-500">
              Entre em contato com o administrador do sistema.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Wifi className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Gerenciamento de APs</h1>
          <p className="text-gray-600 mt-2">Huawei S6730</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Switch
            </label>
            <select
              value={selectedSwitch}
              onChange={(e) => setSelectedSwitch(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              required
            >
              {switches.map((sw) => (
                <option key={sw.id} value={sw.id}>{sw.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Usuário
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Digite seu usuário"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Senha
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Digite sua senha"
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <Button
            variant="primary"
            className="w-full"
            disabled={loading}
          >
            {loading ? 'Entrando...' : 'Entrar'}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Sistema de Gerenciamento Facilitado - SGF</p>
          <p className="font-mono mt-1">Para APs Huawei Via AC - {CHANGELOG_VERSAO_ATUAL} - {ASSINATURA}</p>
        </div>
      </Card>
    </div>
  );
};

// ============= SETUP INICIAL =============

const SetupPage = ({ onSetupComplete }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (password.length < 6) {
      setError('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/auth/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, name })
      });

      const data = await response.json();

      if (data.success) {
        alert('Sistema configurado com sucesso! Faça login com suas credenciais.');
        onSetupComplete();
      } else {
        setError(data.error || 'Erro ao configurar sistema');
      }
    } catch (err) {
      setError('Erro de conexão com o servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Wifi className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">Configuração Inicial</h1>
          <p className="text-gray-600 mt-2">Crie o primeiro usuário administrador</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Usuário
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="admin"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Nome Completo
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Nome Sobrenome"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Senha
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Mínimo 6 caracteres"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Confirmar Senha
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="Digite a senha novamente"
              required
            />
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <Button
            variant="primary"
            className="w-full"
            disabled={loading}
          >
            {loading ? 'Configurando...' : 'Criar Administrador'}
          </Button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-600">
          <p>Este usuário terá privilégios de administrador</p>
        </div>
      </Card>
    </div>
  );
};

// ============= LAYOUT PRINCIPAL =============

const ChangelogModal = () => {
  const entradaAtual = CHANGELOG_HISTORICO.find(e => e.versao === CHANGELOG_VERSAO_ATUAL);
  const marcador = `${CHANGELOG_VERSAO_ATUAL}:${entradaAtual?.itens?.length || 0}`;
  const [aberto, setAberto] = useState(false);

  useEffect(() => {
    if (!entradaAtual) return;
    let visto = null;
    try { visto = localStorage.getItem(CHANGELOG_CHAVE_VISTO); } catch (err) { /* storage indisponível: sempre mostra */ }
    if (visto !== marcador) setAberto(true);
  }, []);

  useEffect(() => {
    if (!aberto) return;
    const aoTeclar = (ev) => { if (ev.key === 'Escape') setAberto(false); };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [aberto]);

  if (!aberto || !entradaAtual) return null;

  const naoMostrarNovamente = () => {
    try { localStorage.setItem(CHANGELOG_CHAVE_VISTO, marcador); } catch (err) { /* sem storage, sem persistência */ }
    setAberto(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center p-6 z-50"
      onClick={(e) => { if (e.target === e.currentTarget) setAberto(false); }}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-7" role="dialog" aria-modal="true">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-bold text-gray-800">Novidades</h2>
        </div>

        <ul className="max-h-[50vh] overflow-y-auto mb-5 space-y-4">
          {CHANGELOG_HISTORICO.map((entrada, idx) => (
            <li key={entrada.versao} className="relative pl-5">
              {idx < CHANGELOG_HISTORICO.length - 1 && (
                <span className="absolute left-[3px] top-4 bottom-[-1rem] w-px bg-gray-200" />
              )}
              <span className={`absolute left-0 top-1.5 w-2 h-2 rounded-full ${entrada.versao === CHANGELOG_VERSAO_ATUAL ? 'bg-blue-600' : 'bg-gray-300'}`} />
              <div className="flex items-baseline gap-2 mb-1">
                <span className={`font-mono font-bold text-sm ${entrada.versao === CHANGELOG_VERSAO_ATUAL ? 'text-blue-600' : 'text-gray-800'}`}>
                  {entrada.versao}
                </span>
                <span className="font-mono text-xs text-gray-400">{entrada.data}</span>
              </div>
              <ul className="list-disc pl-4 text-sm text-gray-600 space-y-1">
                {entrada.itens.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
            </li>
          ))}
        </ul>

        <div className="flex gap-3">
          <Button variant="secondary" className="flex-1" onClick={() => setAberto(false)}>
            Fechar
          </Button>
          <Button variant="primary" className="flex-1" onClick={naoMostrarNovamente}>
            Não mostrar novamente
          </Button>
        </div>
      </div>
    </div>
  );
};

const Layout = ({ children, user, onLogout }) => {
  const navigate = useNavigate();
  const switchName = localStorage.getItem('switch_name') || 'Switch';

  return (
    <div className="min-h-screen bg-gray-50">
      <ChangelogModal />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <Wifi className="w-8 h-8 text-blue-600" />
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-gray-800">Gerenciamento de APs</h1>
                <p className="text-xs text-gray-500">Huawei S6730</p>
              </div>
            </div>

            {/* Switch conectado */}
            <div className="flex items-center gap-2 px-2 sm:px-3 py-1 bg-green-50 border border-green-200 rounded-lg">
              <Activity className="w-4 h-4 text-green-600" />
              <span className="text-sm text-green-700 font-medium hidden sm:inline">{switchName}</span>
            </div>

              <div className="flex items-center gap-2 sm:gap-4">
                <div className="hidden sm:flex items-center gap-2 text-sm text-gray-600">
                  <User className="w-4 h-4" />
                  <span className="font-medium">{user?.name}</span>
                </div>
                {user?.role === 'admin' && (
                  <Button variant="secondary" onClick={() => navigate('/settings')} className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    <span className="hidden sm:inline">Configurações</span>
                  </Button>
                )}
                <Button variant="secondary" onClick={onLogout} className="flex items-center gap-2">
                  <LogOut className="w-4 h-4" />
                  <span className="hidden sm:inline">Sair</span>
                </Button>
              </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 sm:gap-6">
            <NavLink to="/" icon={Home}>Dashboard</NavLink>
            <NavLink to="/groups" icon={Folder}>Grupos</NavLink>
            <NavLink to="/aps" icon={Radio}>Access Points</NavLink>
            {user?.role === 'admin' && (
              <NavLink to="/users" icon={Users}>Usuários</NavLink>
            )}
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

const NavLink = ({ to, icon: Icon, children }) => {
  const navigate = useNavigate();
  const isActive = window.location.pathname === to;

  return (
    <button
      onClick={() => navigate(to)}
      className={`flex items-center gap-2 px-2 sm:px-4 py-3 border-b-2 transition-colors ${
        isActive
          ? 'border-blue-600 text-blue-600 font-medium'
          : 'border-transparent text-gray-600 hover:text-gray-800 hover:border-gray-300'
      }`}
    >
      <Icon className="w-4 h-4" />
      <span className="hidden sm:inline">{children}</span>
    </button>
  );
};

// ============= DASHBOARD =============

const Dashboard = ({ token }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchDashboard();
  }, []);

const fetchDashboard = async (isRefresh = false) => {
  if (isRefresh) setRefreshing(true);
  try {
    const response = await fetch(`${API_URL}/dashboard`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    setStats(data);
  } catch (err) {
    console.error('Erro ao carregar dashboard:', err);
  } finally {
    setLoading(false);
    if (isRefresh) setRefreshing(false);
  }
};

  if (loading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
		<div className="flex justify-between items-center">
		  <h2 className="text-2xl font-bold text-gray-800">Dashboard</h2>

		  <div className="flex gap-2">
      <Button
        onClick={() => fetchDashboard(true)}
        variant="secondary"
        className="flex items-center gap-2"
        disabled={refreshing || saving}
      >
        {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
        {refreshing ? 'Atualizando...' : 'Atualizar'}
      </Button>

      <Button
        variant="primary"
        className="flex items-center gap-2"
        disabled={saving || refreshing}
        onClick={async () => {
        const confirmSave = window.confirm(
          'Deseja salvar a configuração atual no switch?'
        );
        if (!confirmSave) return;

        setSaving(true);
        try {
          const response = await fetch(`${API_URL}/switch/save`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
          });

          const data = await response.json();

          if (data.success) {
          alert('Configuração salva com sucesso no switch.');
          } else {
          alert(data.error || 'Erro ao salvar configuração.');
          }
        } catch (err) {
          alert('Erro de comunicação com o backend.');
        } finally {
          setSaving(false);
        }
        }}
      >
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {saving ? 'Salvando...' : 'Salvar Configuração'}
      </Button>
		  </div>
		</div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <StatCard
          title="Total de APs"
          value={stats?.total_aps || 0}
          icon={Radio}
          color="blue"
        />
        <StatCard
          title="Online"
          value={stats?.online || 0}
          icon={Activity}
          color="green"
        />
        <StatCard
          title="Offline"
          value={stats?.offline || 0}
          icon={AlertCircle}
          color="red"
        />
        <StatCard
          title="Idle"
          value={stats?.idle || 0}
          icon={Activity}
          color="yellow"
        />
      </div>

      {/* Alertas */}
      {stats?.default_aps > 0 && (
        <Card className="border-l-4 border-yellow-500">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
            <div className="flex-1">
              <h3 className="font-semibold text-gray-800 mb-1">
                APs Pendentes de Configuração
              </h3>
              <p className="text-gray-600 text-sm mb-3">
                Existem {stats.default_aps} APs no grupo "default" aguardando configuração.
              </p>
              <Button
                variant="primary"
                onClick={() => navigate('/aps?group=default')}
                className="text-sm"
              >
                Ver APs Pendentes
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color }) => {
  const colors = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    red: 'bg-red-100 text-red-600',
    yellow: 'bg-yellow-100 text-yellow-600'
  };

  return (
    <Card>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-600 mb-1">{title}</p>
          <p className="text-3xl font-bold text-gray-800">{value}</p>
        </div>
        <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colors[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Card>
  );
};

// ============= GRUPOS =============

const GroupsPage = ({ token }) => {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showFilter, setShowFilter] = useState('com_aps');
  const [searchTerm, setSearchTerm] = useState('');
  const navigate = useNavigate();

  const filteredGroups = groups
    .filter(g => showFilter === 'com_aps' ? g.count > 0 : true)
    .filter(g => g.name.toLowerCase().includes(searchTerm.toLowerCase()));

  useEffect(() => {
    fetchGroups();
  }, []);

const fetchGroups = async (isRefresh = false) => {
  if (isRefresh) setRefreshing(true);
  try {
    const response = await fetch(`${API_URL}/groups`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    setGroups(data.data || []);
  } catch (err) {
    console.error('Erro ao carregar grupos:', err);
  } finally {
    setLoading(false);
    if (isRefresh) setRefreshing(false);
  }
};

  if (loading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Grupos de APs</h2>
        <div className="flex gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar grupo..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            />
          </div>
          <select
            value={showFilter}
            onChange={(e) => setShowFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          >
            <option value="com_aps">Com APs</option>
            <option value="todos">Todos</option>
          </select>
          <Button onClick={() => fetchGroups(true)} variant="secondary" className="flex items-center gap-2" disabled={refreshing}>
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </Button>
        </div>
      </div>

		<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
		  {filteredGroups.map((group) => (
			<button
			  key={group.name}
			  type="button"
			  className="text-left w-full"
			  onClick={() => navigate(`/aps?group=${group.name}`)}
			>
			  <Card className="hover:shadow-lg transition-shadow cursor-pointer">
				<div className="flex items-center gap-4">
				  <div className="w-16 h-16 bg-blue-100 rounded-lg flex items-center justify-center">
					<Folder className="w-8 h-8 text-blue-600" />
				  </div>
				  <div className="flex-1">
					<h3 className="font-semibold text-gray-800 text-lg">{group.name}</h3>
					<p className="text-gray-600 text-sm mt-1">
					  {group.count} Access Points
					</p>
				  </div>
				  <ChevronRight className="w-6 h-6 text-gray-400" />
				</div>
			  </Card>
			</button>
		  ))}
		</div>
    </div>
  );
};

// ============= ACCESS POINTS =============

const APsPage = ({ token, user }) => {
  const [aps, setAps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [groupFilter, setGroupFilter] = useState('');
  const navigate = useNavigate();
  
  const canEdit = user?.role !== 'read';

  // Estados para seleção múltipla e movimentação
  const [selectedAPs, setSelectedAPs] = useState([]);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [targetGroup, setTargetGroup] = useState('');
  const [movingAPs, setMovingAPs] = useState(false);

  // Estados para remover do grupo e deletar
  const [showRemoveGroupModal, setShowRemoveGroupModal] = useState(false);
  const [removingFromGroup, setRemovingFromGroup] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingAPs, setDeletingAPs] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const group = params.get('group');
    if (group) setGroupFilter(group);
    fetchAPs(group);
  }, []);

const fetchAPs = async (group = null, isRefresh = false) => {
  if (isRefresh) setRefreshing(true);
  try {
    const url = group ? `${API_URL}/aps?group=${group}` : `${API_URL}/aps`;
    const response = await fetch(url, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    setAps(data.data || []);
  } catch (err) {
    console.error('Erro ao carregar APs:', err);
  } finally {
    setLoading(false);
    if (isRefresh) setRefreshing(false);
  }
};

  const filteredAPs = aps.filter(ap => {
    const matchesSearch = 
      ap.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ap.mac.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesStatus = statusFilter === 'all' || ap.state.toLowerCase() === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => {
    const variants = {
      'online': 'success',
      'offline': 'danger',
      'idle': 'warning'
    };
    return <Badge variant={variants[status.toLowerCase()]}>{status}</Badge>;
  };

  // Funções de seleção
  const toggleSelectAP = (apId) => {
    setSelectedAPs(prev => 
      prev.includes(apId) 
        ? prev.filter(id => id !== apId)
        : [...prev, apId]
    );
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = filteredAPs.map(ap => ap.id);
    const allVisibleSelected = visibleIds.every(id => selectedAPs.includes(id));
    
    if (allVisibleSelected) {
      // Remove todos os visíveis da seleção
      setSelectedAPs(prev => prev.filter(id => !visibleIds.includes(id)));
    } else {
      // Adiciona todos os visíveis à seleção
      setSelectedAPs(prev => [...new Set([...prev, ...visibleIds])]);
    }
  };

  const clearSelection = () => {
    setSelectedAPs([]);
  };

  // Funções de movimentação
  const fetchAvailableGroups = async () => {
    try {
      const response = await fetch(`${API_URL}/groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAvailableGroups(data.data || []);
    } catch (err) {
      console.error('Erro ao carregar grupos:', err);
    }
  };

  const openMoveModal = () => {
    fetchAvailableGroups();
    setTargetGroup('');
    setShowMoveModal(true);
  };

  const handleMoveSelectedAPs = async () => {
    if (!targetGroup || selectedAPs.length === 0) return;
    
    setMovingAPs(true);

    try {
      const response = await fetch(`${API_URL}/aps/move-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          ap_ids: selectedAPs, 
          group: targetGroup 
        })
      });
      
      const data = await response.json();
      
      setMovingAPs(false);
      setShowMoveModal(false);
      
      if (data.success) {
        if (data.error_count === 0) {
          alert(`${data.success_count} AP(s) movido(s) com sucesso para o grupo "${targetGroup}"!`);
        } else {
          alert(`${data.success_count} AP(s) movido(s) com sucesso, ${data.error_count} erro(s).`);
        }
      } else {
        alert(data.error || 'Erro ao mover APs');
      }
    } catch (err) {
      setMovingAPs(false);
      setShowMoveModal(false);
      alert('Erro de comunicação com o servidor');
    }
    
    setSelectedAPs([]);
    fetchAPs(groupFilter, true);
  };

  // Remover APs do grupo (volta para default)
  const handleRemoveFromGroup = async () => {
    if (selectedAPs.length === 0) return;
    
    setRemovingFromGroup(true);

    try {
      const response = await fetch(`${API_URL}/aps/remove-group-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ap_ids: selectedAPs })
      });
      
      const data = await response.json();
      
      setRemovingFromGroup(false);
      setShowRemoveGroupModal(false);
      
      if (data.success) {
        if (data.error_count === 0) {
          alert(`${data.success_count} AP(s) removido(s) do grupo com sucesso!`);
        } else {
          alert(`${data.success_count} AP(s) removido(s), ${data.error_count} erro(s).`);
        }
      } else {
        alert(data.error || 'Erro ao remover APs do grupo');
      }
    } catch (err) {
      setRemovingFromGroup(false);
      setShowRemoveGroupModal(false);
      alert('Erro de comunicação com o servidor');
    }
    
    setSelectedAPs([]);
    fetchAPs(groupFilter, true);
  };

  // Deletar APs do sistema
  const handleDeleteAPs = async () => {
    if (selectedAPs.length === 0) return;
    
    setDeletingAPs(true);

    try {
      const response = await fetch(`${API_URL}/aps/delete-batch`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ ap_ids: selectedAPs })
      });
      
      const data = await response.json();
      
      setDeletingAPs(false);
      setShowDeleteModal(false);
      
      if (data.success) {
        alert(data.message);
      } else {
        alert(data.error || 'Erro ao deletar APs');
      }
    } catch (err) {
      setDeletingAPs(false);
      setShowDeleteModal(false);
      alert('Erro de comunicação com o servidor');
    }
    
    setSelectedAPs([]);
    fetchAPs(groupFilter, true);
  };

  if (loading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Access Points</h2>
          {groupFilter && (
            <p className="text-sm text-gray-600 mt-1">Grupo: {groupFilter}</p>
          )}
        </div>
        <Button onClick={() => fetchAPs(groupFilter, true)} variant="secondary" className="flex items-center gap-2" disabled={refreshing}>
          {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {refreshing ? 'Atualizando...' : 'Atualizar'}
        </Button>
      </div>

      {/* Filtros */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Buscar
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="MAC, Nome ou Serial..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">Todos</option>
              <option value="online">Online</option>
              <option value="offline">Offline</option>
              <option value="idle">Idle</option>
            </select>
          </div>
        </div>
      </Card>

      {/* Barra de Ações para Seleção */}
      <Card>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
          <div className="flex items-center gap-2 sm:gap-4">
            <span className="text-sm text-gray-600">
              {selectedAPs.length > 0 ? (
                <span className="font-medium text-blue-600">{selectedAPs.length} AP(s)</span>
              ) : (
                'Nenhum selecionado'
              )}
            </span>
            {selectedAPs.length > 0 && (
              <Button variant="secondary" className="text-sm" onClick={clearSelection}>
                Limpar
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button 
              variant="primary" 
              className="flex items-center gap-2"
              onClick={openMoveModal}
              disabled={selectedAPs.length === 0}
            >
              <Move className="w-4 h-4" />
              <span className="hidden sm:inline">Mover</span>
            </Button>
            <Button 
              variant="secondary" 
              className="flex items-center gap-2"
              onClick={() => setShowRemoveGroupModal(true)}
              disabled={selectedAPs.length === 0}
            >
              <Folder className="w-4 h-4" />
              <span className="hidden sm:inline">Remover do Grupo</span>
            </Button>
            <Button 
              variant="danger" 
              className="flex items-center gap-2"
              onClick={() => setShowDeleteModal(true)}
              disabled={selectedAPs.length === 0}
            >
              <Trash2 className="w-4 h-4" />
              <span className="hidden sm:inline">Deletar</span>
            </Button>
          </div>
        </div>
      </Card>

      {/* Lista de APs */}
      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                {canEdit && (
                  <th className="py-3 px-4 w-12">
                    <input
                      type="checkbox"
                      checked={filteredAPs.length > 0 && filteredAPs.every(ap => selectedAPs.includes(ap.id))}
                      onChange={toggleSelectAllVisible}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                )}
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Nome</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">MAC</th>
                
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Grupo</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredAPs.map((ap) => (
                <tr key={ap.mac} className={`border-b border-gray-100 hover:bg-gray-50 ${canEdit && selectedAPs.includes(ap.id) ? 'bg-blue-50' : ''}`}>
                  {canEdit && (
                    <td className="py-3 px-4">
                      <input
                        type="checkbox"
                        checked={selectedAPs.includes(ap.id)}
                        onChange={() => toggleSelectAP(ap.id)}
                        className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                  )}
                  <td className="py-3 px-4 font-medium text-gray-800">{ap.name}</td>
                  <td className="py-3 px-4 text-gray-600 font-mono text-sm">{ap.mac}</td>
                  <td className="py-3 px-4 text-gray-600">{ap.group}</td>
                  <td className="py-3 px-4">{getStatusBadge(ap.state)}</td>
                  <td className="py-3 px-4">
                    <Button
                      variant="secondary"
                      className="text-sm"
                      onClick={() => navigate(`/aps/${ap.id}`, { state: { fromGroup: groupFilter } })}
                    >
                      Detalhes
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {filteredAPs.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Nenhum AP encontrado
            </div>
          )}
        </div>
      </Card>

      {/* Modal Mover APs */}
      {showMoveModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Mover {selectedAPs.length} AP(s)
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Grupo de Destino
                </label>
                <select
                  value={targetGroup}
                  onChange={(e) => setTargetGroup(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione um grupo...</option>
                  {availableGroups.map((g) => (
                    <option key={g.name} value={g.name}>{g.name}</option>
                  ))}
                </select>
              </div>
              <p className="text-sm text-gray-500">
                Os APs selecionados serão movidos para o grupo escolhido.
              </p>
            </div>
            <div className="flex gap-2 mt-6">
              <Button 
                variant="primary" 
                className="flex-1 flex items-center justify-center gap-2" 
                onClick={handleMoveSelectedAPs} 
                disabled={movingAPs || !targetGroup}
              >
                {movingAPs && <Loader2 className="w-4 h-4 animate-spin" />}
                {movingAPs ? 'Movendo...' : 'Mover'}
              </Button>
              <Button 
                variant="secondary" 
                className="flex-1" 
                onClick={() => setShowMoveModal(false)} 
                disabled={movingAPs}
              >
                Cancelar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Remover do Grupo */}
      {showRemoveGroupModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Remover {selectedAPs.length} AP(s) do Grupo
            </h3>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Os APs selecionados serão removidos de seus grupos atuais e voltarão para o grupo <strong>"default"</strong>.
              </p>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  Esta ação pode ser revertida movendo os APs novamente para o grupo desejado.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button 
                variant="primary" 
                className="flex-1 flex items-center justify-center gap-2" 
                onClick={handleRemoveFromGroup} 
                disabled={removingFromGroup}
              >
                {removingFromGroup && <Loader2 className="w-4 h-4 animate-spin" />}
                {removingFromGroup ? 'Removendo...' : 'Confirmar'}
              </Button>
              <Button 
                variant="secondary" 
                className="flex-1" 
                onClick={() => setShowRemoveGroupModal(false)} 
                disabled={removingFromGroup}
              >
                Cancelar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Deletar APs */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-semibold text-red-600 mb-4">
              Deletar {selectedAPs.length} AP(s)
            </h3>
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Os APs selecionados serão <strong>permanentemente removidos</strong> do sistema.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">
                  <strong>Atenção:</strong> Esta ação não pode ser desfeita. As licenças serão liberadas e os APs precisarão ser reprovisionados caso voltem à rede.
                </p>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button 
                variant="danger" 
                className="flex-1 flex items-center justify-center gap-2" 
                onClick={handleDeleteAPs} 
                disabled={deletingAPs}
              >
                {deletingAPs && <Loader2 className="w-4 h-4 animate-spin" />}
                {deletingAPs ? 'Deletando...' : 'Deletar'}
              </Button>
              <Button 
                variant="secondary" 
                className="flex-1" 
                onClick={() => setShowDeleteModal(false)} 
                disabled={deletingAPs}
              >
                Cancelar
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============= DETALHES DO AP =============

const APDetailsPage = ({ token, user }) => {
  const { id } = useParams();
  const [ap, setAp] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const fromGroup = location.state?.fromGroup;
  
  const canEdit = user?.role !== 'read';
  
  // Estados para edição
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [savingName, setSavingName] = useState(false);

  useEffect(() => {
    fetchAPDetails();
  }, [id]);

  const fetchAPDetails = async () => {
    try {
      const response = await fetch(`${API_URL}/aps/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAp(data.data);
    } catch (err) {
      console.error('Erro ao carregar detalhes do AP:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  if (!ap) {
    return <div className="text-center py-12">AP não encontrado</div>;
  }

const handleRename = async () => {
  setSavingName(true);
  try {
	const response = await fetch(`${API_URL}/aps/${id}/rename`, {
	  method: 'POST',
	  headers: {
		'Content-Type': 'application/json',
		'Authorization': `Bearer ${token}`
	  },
	  body: JSON.stringify({ name: newName })
	});
	
	const data = await response.json();
	
	if (data.success) {
	  setEditingName(false);
	  setNewName('');
	  fetchAPDetails();
	} else {
	  alert(data.error || 'Erro ao renomear AP');
	}
  } catch (err) {
	alert('Erro de comunicação com o servidor');
  } finally {
	setSavingName(false);
  }
};

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="secondary" onClick={() => navigate(fromGroup ? `/aps?group=${fromGroup}` : '/aps')}>
          ← Voltar
        </Button>
        <h2 className="text-2xl font-bold text-gray-800">{ap.name}</h2>
      </div>

		{/* Informações Básicas */}
		<Card>
		  <div className="flex justify-between items-center mb-4">
			<h3 className="text-lg font-semibold text-gray-800">Informações Básicas</h3>
			<div className="flex gap-2">
			  {canEdit && !editingName && (
				<Button
				  variant="primary"
				  className="text-sm"
				  onClick={() => {
					setEditingName(true);
					setNewName(ap.name);
				  }}
				>
				  Editar Nome
				</Button>
			  )}
			</div>
		  </div>
		  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
			<div>
			  <p className="text-sm text-gray-600 mb-1">Nome</p>
			  {editingName ? (
				<div className="flex gap-2 items-center">
				  <input
					type="text"
					value={newName}
					onChange={(e) => setNewName(e.target.value)}
					className="border border-gray-300 rounded px-2 py-1 flex-1"
				  />
          <Button variant="success" className="text-sm flex items-center gap-1" onClick={handleRename} disabled={savingName}>
            {savingName && <Loader2 className="w-3 h-3 animate-spin" />}
            {savingName ? 'Salvando...' : 'Salvar'}
          </Button>
				  <Button variant="secondary" className="text-sm" onClick={() => setEditingName(false)}>Cancelar</Button>
				</div>
			  ) : (
				<p className="font-medium text-gray-800">{ap.name}</p>
			  )}
			</div>
			<InfoItem label="MAC Address" value={ap.mac} />
			<InfoItem label="Serial" value={ap.serial} />
			<InfoItem label="Modelo" value={ap.type} />
			<InfoItem label="Grupo" value={ap.group} />
			<InfoItem label="País" value={ap.country_code} />
			<InfoItem label="Status" value={<Badge variant="success">{ap.state}</Badge>} />
		  </div>
		</Card>

      {/* Rádios */}
      {ap.radios && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Configuração de Rádios</h3>
          <div className="space-y-4">
            {ap.radios.map((radio, idx) => (
              <div key={idx} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Signal className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-gray-800">{radio.band}</span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Canal:</span>
                    <span className="ml-2 font-medium">{radio.channel}</span>
                  </div>
                  <div>
                    <span className="text-gray-600">Largura:</span>
                    <span className="ml-2 font-medium">{radio.bandwidth}</span>
                  </div>
	  			  <div>
					<span className="text-gray-600">Tipo:</span>
					<span className="ml-2 font-medium">{radio.type}</span>
				  </div>
				  <div>
					<span className="text-gray-600">EIRP:</span>
					<span className="ml-2 font-medium">{radio.eirp}</span>
				  </div>
                 </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* SSIDs */}
      {ap.ssids && ap.ssids.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">SSIDs Configurados</h3>
          <div className="space-y-2">
            {ap.ssids.map((ssid, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <Wifi className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-gray-800">{ssid.name}</span>
                </div>
                <Badge variant="default">{ssid.band}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Clientes Conectados */}
      {ap.clients && ap.clients.length > 0 && (
        <Card>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            Clientes Conectados ({ap.clients.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">MAC</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">SSID</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Banda</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">RSSI</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">IP</th>
                </tr>
              </thead>
              <tbody>
                {ap.clients.map((client, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="py-3 px-4 font-mono text-sm text-gray-600">{client.mac}</td>
                    <td className="py-3 px-4 text-gray-800">{client.ssid}</td>
                    <td className="py-3 px-4 text-gray-600">{client.band}</td>
                    <td className="py-3 px-4 text-gray-600">{client.rssi} dBm</td>
                    <td className="py-3 px-4 font-mono text-sm text-gray-600">{client.ip}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
};



const InfoItem = ({ label, value }) => (
  <div>
    <p className="text-sm text-gray-600 mb-1">{label}</p>
    <p className="font-medium text-gray-800">{value}</p>
  </div>
);

// ============= USUÁRIOS =============

const UsersPage = ({ token, user }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ username: '', password: '', name: '', role: 'operator' });
  const [saving, setSaving] = useState(false);

  // Redireciona não-admin para dashboard
  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchUsers();
    }
  }, [user]);

  const fetchUsers = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setUsers(data.data || []);
    } catch (err) {
      console.error('Erro ao carregar usuários:', err);
    } finally {
      setLoading(false);
      if (isRefresh) setRefreshing(false);
    }
  };

  const handleAddUser = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Usuário adicionado com sucesso!');
        setShowAddModal(false);
        setFormData({ username: '', password: '', name: '', role: 'operator' });
        fetchUsers();
      } else {
        alert(data.error || 'Erro ao adicionar usuário');
      }
    } catch (err) {
      alert('Erro de comunicação com o servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleEditUser = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/users/${editingUser.username}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Usuário editado com sucesso!');
        setShowEditModal(false);
        setEditingUser(null);
        setFormData({ username: '', password: '', name: '', role: 'operator' });
        fetchUsers();
      } else {
        alert(data.error || 'Erro ao editar usuário');
      }
    } catch (err) {
      alert('Erro de comunicação com o servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm(`Tem certeza que deseja excluir o usuário "${username}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/users/${username}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Usuário excluído com sucesso!');
        fetchUsers();
      } else {
        alert(data.error || 'Erro ao excluir usuário');
      }
    } catch (err) {
      alert('Erro de comunicação com o servidor');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Usuários</h2>
        <div className="flex gap-2">
          <Button onClick={() => fetchUsers(true)} variant="secondary" className="flex items-center gap-2" disabled={refreshing}>
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>Adicionar Usuário</Button>
        </div>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Usuário</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Nome</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Função</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.username} className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-800">{user.username}</td>
                  <td className="py-3 px-4 text-gray-600">{user.name}</td>
                  <td className="py-3 px-4">
                    <Badge variant={user.role === 'admin' ? 'success' : user.role === 'read' ? 'warning' : 'default'}>
                      {user.role === 'admin' ? 'Administrador' : user.role === 'read' ? 'Somente Leitura' : 'Operador'}
                    </Badge>
                  </td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <Button 
                        variant="secondary" 
                        className="text-sm"
                        onClick={() => {
                          setEditingUser(user);
                          setFormData({ username: user.username, password: '', name: user.name, role: user.role });
                          setShowEditModal(true);
                        }}
                      >
                        Editar
                      </Button>
                      <Button 
                        variant="danger" 
                        className="text-sm"
                        onClick={() => handleDeleteUser(user.username)}
                      >
                        Excluir
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal Adicionar Usuário */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Adicionar Usuário</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Usuário</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="nome.usuario"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome Completo</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nome Sobrenome"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Senha</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="********"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Função</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="read">Somente Leitura</option>
                  <option value="operator">Operador</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="primary" className="flex-1 flex items-center justify-center gap-2" onClick={handleAddUser} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button variant="secondary" className="flex-1" onClick={() => {
                setShowAddModal(false);
                setFormData({ username: '', password: '', name: '', role: 'operator' });
              }} disabled={saving}>
                Cancelar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Editar Usuário */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Editar Usuário</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Usuário</label>
                <input
                  type="text"
                  value={formData.username}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome Completo</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nova Senha (deixe em branco para não alterar)</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="********"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Função</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="read">Somente Leitura</option>
                  <option value="operator">Operador</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="primary" className="flex-1 flex items-center justify-center gap-2" onClick={handleEditUser} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button variant="secondary" className="flex-1" onClick={() => {
                setShowEditModal(false);
                setEditingUser(null);
                setFormData({ username: '', password: '', name: '', role: 'operator' });
              }} disabled={saving}>
                Cancelar
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============= CONFIGURAÇÕES =============

const SettingsPage = ({ token, user }) => {
  const navigate = useNavigate();
  const [switches, setSwitches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSwitch, setEditingSwitch] = useState(null);
  const [formData, setFormData] = useState({ name: '', host: '', port: 22, username: '', password: '' });
  const [saving, setSaving] = useState(false);

  // Redireciona não-admin para dashboard
  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
    }
  }, [user, navigate]);

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchSwitches();
    }
  }, [user]);

  const fetchSwitches = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    try {
      const response = await fetch(`${API_URL}/switches`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSwitches(data.data || []);
    } catch (err) {
      console.error('Erro ao carregar switches:', err);
    } finally {
      setLoading(false);
      if (isRefresh) setRefreshing(false);
    }
  };

  const handleAddSwitch = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/switches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Switch adicionado com sucesso!');
        setShowAddModal(false);
        setFormData({ name: '', host: '', port: 22, username: '', password: '' });
        fetchSwitches();
      } else {
        alert(data.error || 'Erro ao adicionar switch');
      }
    } catch (err) {
      alert('Erro de comunicação com o servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleEditSwitch = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/switches/${editingSwitch.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Switch editado com sucesso!');
        setShowEditModal(false);
        setEditingSwitch(null);
        setFormData({ name: '', host: '', port: 22, username: '', password: '' });
        fetchSwitches();
      } else {
        alert(data.error || 'Erro ao editar switch');
      }
    } catch (err) {
      alert('Erro de comunicação com o servidor');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteSwitch = async (switchId, switchName) => {
    if (!window.confirm(`Tem certeza que deseja excluir o switch "${switchName}"?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/switches/${switchId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      const data = await response.json();
      
      if (data.success) {
        alert('Switch excluído com sucesso!');
        fetchSwitches();
      } else {
        alert(data.error || 'Erro ao excluir switch');
      }
    } catch (err) {
      alert('Erro de comunicação com o servidor');
    }
  };

  if (loading) {
    return <div className="text-center py-12">Carregando...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-800">Configurações de Switches</h2>
        <div className="flex gap-2">
          <Button onClick={() => fetchSwitches(true)} variant="secondary" className="flex items-center gap-2" disabled={refreshing}>
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {refreshing ? 'Atualizando...' : 'Atualizar'}
          </Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>Adicionar Switch</Button>
        </div>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Nome</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Host</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Porta</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Usuário</th>
                <th className="text-left py-3 px-4 font-semibold text-gray-700">Ações</th>
              </tr>
            </thead>
            <tbody>
              {switches.map((sw) => (
                <tr key={sw.id} className="border-b border-gray-100">
                  <td className="py-3 px-4 font-medium text-gray-800">{sw.name}</td>
                  <td className="py-3 px-4 text-gray-600">{sw.host}</td>
                  <td className="py-3 px-4 text-gray-600">{sw.port}</td>
                  <td className="py-3 px-4 text-gray-600">{sw.username}</td>
                  <td className="py-3 px-4">
                    <div className="flex gap-2">
                      <Button 
                        variant="secondary" 
                        className="text-sm"
                        onClick={() => {
                          setEditingSwitch(sw);
                          setFormData({ name: sw.name, host: sw.host, port: sw.port, username: sw.username, password: '' });
                          setShowEditModal(true);
                        }}
                      >
                        Editar
                      </Button>
                      <Button 
                        variant="danger" 
                        className="text-sm"
                        onClick={() => handleDeleteSwitch(sw.id, sw.name)}
                      >
                        Excluir
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Modal Adicionar Switch */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Adicionar Switch</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="SW-Core-01"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Host (IP ou FQDN)</label>
                <input
                  type="text"
                  value={formData.host}
                  onChange={(e) => setFormData({...formData, host: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="192.168.1.1 ou switch.local"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Porta SSH</label>
                <input
                  type="number"
                  value={formData.port}
                  onChange={(e) => setFormData({...formData, port: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="22"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Usuário SSH</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="admin"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Senha SSH</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="********"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="primary" className="flex-1 flex items-center justify-center gap-2" onClick={handleAddSwitch} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button variant="secondary" className="flex-1" onClick={() => {
                setShowAddModal(false);
                setFormData({ name: '', host: '', port: 22, username: '', password: '' });
              }} disabled={saving}>
                Cancelar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Editar Switch */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Editar Switch</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nome</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Host (IP ou FQDN)</label>
                <input
                  type="text"
                  value={formData.host}
                  onChange={(e) => setFormData({...formData, host: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Porta SSH</label>
                <input
                  type="number"
                  value={formData.port}
                  onChange={(e) => setFormData({...formData, port: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Usuário SSH</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nova Senha SSH (deixe em branco para não alterar)</label>
                <input
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="********"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button variant="primary" className="flex-1 flex items-center justify-center gap-2" onClick={handleEditSwitch} disabled={saving}>
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {saving ? 'Salvando...' : 'Salvar'}
              </Button>
              <Button variant="secondary" className="flex-1" onClick={() => {
                setShowEditModal(false);
                setEditingSwitch(null);
                setFormData({ name: '', host: '', port: 22, username: '', password: '' });
              }} disabled={saving}>
                Cancelar
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// ============= APP PRINCIPAL =============

function App() {
	const [token, setToken] = useState(localStorage.getItem('token'));
	const [user, setUser] = useState(
	  JSON.parse(localStorage.getItem('user') || 'null')
	);
	const [needsSetup, setNeedsSetup] = useState(null);

	useEffect(() => {
		checkSetupStatus();
	}, []);

	const checkSetupStatus = async () => {
		try {
			const response = await fetch(`${API_URL}/auth/setup-status`);
			const data = await response.json();
			setNeedsSetup(data.needs_setup);
		} catch (err) {
			console.error('Erro ao verificar status do setup:', err);
			setNeedsSetup(false);
		}
	};

	const handleLogin = (newToken, newUser) => {
	  setToken(newToken);
	  setUser(newUser);
	};

	const handleLogout = () => {
	  localStorage.removeItem('token');
	  localStorage.removeItem('user');
	  localStorage.removeItem('switch_id');
	  localStorage.removeItem('switch_name');
	  setToken(null);
	  setUser(null);
	};

	const handleSetupComplete = () => {
		setNeedsSetup(false);
	};

	// Aguarda verificação de setup
	if (needsSetup === null) {
		return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
			<div className="text-center">
				<Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
				<p className="text-gray-600">Carregando...</p>
			</div>
		</div>;
	}

	// Mostra tela de setup se necessário
	if (needsSetup) {
		return <SetupPage onSetupComplete={handleSetupComplete} />;
	}

	// Mostra login se não autenticado
	if (!token) {
		return <LoginPage onLogin={handleLogin} />;
	}

	// Mostra aplicação principal
	return (
		<Router>
			<Layout user={user} onLogout={handleLogout}>
        <Routes>
          <Route path="/" element={<Dashboard token={token} />} />
          <Route path="/groups" element={<GroupsPage token={token} />} />
          <Route path="/aps" element={<APsPage token={token} user={user} />} />
          <Route path="/aps/:id" element={<APDetailsPage token={token} user={user} />} />
          <Route path="/users" element={<UsersPage token={token} user={user} />} />
          <Route path="/settings" element={<SettingsPage token={token} user={user} />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
			</Layout>
		</Router>
	);
}

export default App;